import { useEffect, useState } from 'react';
import { Container, Box, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { ADD_SITE_BTN } from '../../config';
import { PlusFillIcon } from 'src/assets';
import { useDocumentTitle, useResponsive } from 'src/hooks';
import { StyledHeaderSiteContent, StyledSearchBox, addSiteBtnBox } from './style';
import { DesktopSiteTable, MobileSiteList } from '../../components';
import { SitesService } from 'src/services/sites';
import { ADD_SITE_PATH } from 'src/routes/config';
import { useAuthentication } from 'src/features/authentication/context';
import { AccessRoles } from 'src/features/user-account-details/types';
import { useTable } from 'src/components';
import { NewSite } from '../../types/sites';
import { SiteSearchBar } from '../../components/search';
export const SiteManagement = () => {
  const {
    customerId: { value: customerId },
    getCurrentRole,
  } = useAuthentication();
  const isMobile = useResponsive('down', 'sm');
  const buttonSize = isMobile ? 'small' : 'medium';
  const [siteData, setSiteData] = useState<{ value: NewSite[] | null; loaded: boolean }>({
    value: [],
    loaded: false,
  });
  const [startFlag, setStartFlag] = useState(false);
  const [siteDataCount, setSiteDataCount] = useState<number>(0);
  const mobileLimit = 100;
  const [skipId, setSkipId] = useState<string>('');

  const [searchValue, setSearchValue] = useState<string>('');

  const {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    pages,
    setPages,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  useDocumentTitle('Streametric - Sites List');
  useEffect(() => {
    if (!customerId || !startFlag) return;
    const chosenSkipToId = pages.find((obj) => obj.page === page)?.skipTo ?? '';
    setSkipId(chosenSkipToId);

    setSiteData((prevData) => ({ ...prevData, loaded: false }));
    SitesService.getAll({
      customerId: customerId,
      limit: isMobile ? mobileLimit : rowsPerPage,
      skipTo: chosenSkipToId,
      descending: false,
      sortBy: 'siteName',
    })
      .then((data: NewSite[]) => {
        if (data.length > 0) {
          isMobile
            ? setSiteData((prevData) => {
                const newData: { value: NewSite[]; loaded: boolean } = {
                  value: [],
                  loaded: false,
                };
                prevData.value?.forEach((prev) => {
                  newData.value.push(prev);
                });

                data.forEach((alarmEvent) => {
                  newData.value.push(alarmEvent);
                });

                newData.loaded = true;

                return newData;
              })
            : setSiteData({ value: data, loaded: true });
        } else {
          setSiteData({ value: [], loaded: true });
        }
      })
      .catch((error) => {
        setSiteData({ value: null, loaded: true });
        console.error(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, rowsPerPage, page, isMobile]);

  useEffect(() => {
    setPages((prevValue) => {
      if (siteData.value?.length && siteData.value.length > 0) {
        const newValue: { page: number; skipTo: string }[] = [];

        prevValue.forEach((value) => {
          if (!newValue.find((newV) => newV.page === value.page)) {
            newValue.push(value);
          }
        });

        if (
          !newValue.find(
            (value: { page: number; skipTo: string }) =>
              siteData.value && value.skipTo === siteData.value[siteData.value.length - 1].id
          )
        ) {
          newValue.push({
            page: page + 1,
            skipTo: (page + 1 + 1).toString(),
          });
        }

        return newValue;
      }
      return prevValue;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteData]);

  useEffect(() => {
    setPages([{ page: 0, skipTo: '1', siteId: '' }]);
    setSiteData({ value: [], loaded: false });
  }, [rowsPerPage, setPages]);

  useEffect(() => {
    if (!customerId) return;

    SitesService.getCount(customerId)
      .then((count) => {
        setSiteDataCount(count);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [customerId]);

  useEffect(() => {
    setPage(0);
    setRowsPerPage(100);
    setStartFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Container maxWidth={false} disableGutters data-sm="site-management-content">
      <StyledHeaderSiteContent isMobile={isMobile}>
        {getCurrentRole(customerId, null) === AccessRoles.ACCOUNT_ADMIN ||
        getCurrentRole(customerId, null) === AccessRoles.SUPER_ADMIN ? (
          <>
            <Box sx={addSiteBtnBox} component="div">
              <Button
                to={ADD_SITE_PATH + '?customerId=' + customerId}
                component={Link}
                size={buttonSize}
                variant="contained"
                startIcon={<PlusFillIcon />}
                disabled={!siteData.loaded}
                data-sm="add-site-button"
              >
                {ADD_SITE_BTN}
              </Button>
            </Box>
          </>
        ) : null}
      </StyledHeaderSiteContent>
      <StyledSearchBox>
        <SiteSearchBar searchValue={searchValue} setSearchValue={setSearchValue} />
      </StyledSearchBox>
      {isMobile ? (
        <MobileSiteList
          data={siteData}
          dataCount={siteDataCount}
          pageNum={page}
          onChangePage={onChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          customerId={customerId}
          skipId={skipId}
          search={searchValue}
        />
      ) : (
        <DesktopSiteTable
          data={siteData}
          dataCount={siteDataCount}
          pageNum={page}
          onChangePage={onChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          customerId={customerId}
          skipId={skipId}
          search={searchValue}
        />
      )}
    </Container>
  );
};

export default SiteManagement;
